import { useTranslation } from 'react-i18next';
import { AccordionDetails, Checkbox, FormControlLabel, Grid, TextField, Typography } from '@mui/material';
import React, { useState } from 'react';
import { UsersMultiSelect } from '../../../../components/UsersMultiSelect';
import { AccountData, OrderData, OrderStatus, PropelUserModel } from '../../../../../api';
import { Accordion, AccordionPanelsNames, AccordionSummary } from '../Accordion';
import { ManagementPanelSummary } from './ManagementPanelSummary';
import CopyToClipboardButton from '../../../CopyToClipboardButton';
import { useAuth } from '../../../../../session/InternalAuthProvider';

type ManagementPanelProps = {
  isEventOrderPage: boolean;
  users: PropelUserModel[];
  selectedUsersIds: string[];
  setSelectedUsersIds: (ids: string[]) => void;
  comment?: string;
  setComment: (comment: string) => void;
  expanded?: AccordionPanelsNames;
  handleChange: any;
  orderId?: string;
  isOneOfVoucherIntents: boolean;
  shouldUnarchive: boolean;
  setShouldUnarchive: (shouldUnarchive: boolean) => void;
  orderOrVoucher: Partial<OrderData>;
};
export const ManagementPanel = ({
  isEventOrderPage,
  users,
  selectedUsersIds,
  setSelectedUsersIds,
  comment,
  setComment,
  expanded,
  handleChange,
  orderId,
  isOneOfVoucherIntents,
  shouldUnarchive,
  setShouldUnarchive,
  orderOrVoucher,
}: ManagementPanelProps) => {
  const { t } = useTranslation();
  const { authState } = useAuth();

  const [internalComment, setInternalComment] = useState(comment);

  return (
    <Accordion onChange={handleChange} expandedPanelName={expanded} panelName={AccordionPanelsNames.ManagementPanel}>
      <AccordionSummary
        title={t('calendar.edit_order.magmntSection')}
        summaryComponent={
          <ManagementPanelSummary
            orderStatus={orderOrVoucher.status}
            shouldHideUsers={isOneOfVoucherIntents}
            comment={internalComment}
            selectedUsersIds={selectedUsersIds}
            users={users}
            isEventOrderPage={isEventOrderPage}
          />
        }
      />
      <AccordionDetails>
        <Grid container flexDirection="column" gap={2}>
          {!isEventOrderPage && !isOneOfVoucherIntents && (
            <UsersMultiSelect
              users={users}
              selectedUsersIds={selectedUsersIds}
              setSelectedUsersIds={setSelectedUsersIds}
            />
          )}
          <TextField
            multiline
            label={t('calendar.edit_event.comments')}
            type="text"
            value={comment}
            onChange={(e) => {
              setInternalComment(e.target.value);
              setComment(e.target.value);
            }}
          />
          {orderId && (authState.account as unknown as AccountData).returning_customer_settings && (
            <Grid container gap={0.5} flexDirection="column" borderRadius="8px" border="1px solid #c4c4c4" p={1}>
              <Typography fontWeight={700}>{`${t('calendar.order_popper.returningCsutomer')}`}</Typography>
              <Grid container gap={1} alignItems="center">
                {orderId}
                <CopyToClipboardButton width={'18px'} color="grey" text={orderId} />
              </Grid>
            </Grid>
          )}
          {orderId && (
            <Grid container gap={0.5} flexDirection="column" borderRadius="8px" border="1px solid #c4c4c4" p={1}>
              <Typography fontWeight={700}>{t('calendar.edit_order.status')}</Typography>
              <Typography> {t(`order_status.${orderOrVoucher?.status?.toLowerCase()}`)}</Typography>
              {orderOrVoucher?.status === OrderStatus.ARCHIVED && (
                <FormControlLabel
                  control={<Checkbox />}
                  checked={shouldUnarchive}
                  label={
                    <Grid container gap={1}>
                      <Typography>{t('calendar.edit_order.changeBackTo')}</Typography>
                      <Typography fontWeight={700}>
                        {t(`order_status.${orderOrVoucher?.last_status?.toLowerCase()}`)}
                      </Typography>
                    </Grid>
                  }
                  onChange={() => {
                    setShouldUnarchive(!shouldUnarchive);
                  }}
                />
              )}
            </Grid>
          )}

          {orderOrVoucher && (
            <Grid container gap={0.5} flexDirection="column" borderRadius="8px" border="1px solid #c4c4c4" p={1}>
              <Typography fontWeight={700}>{t('calendar.edit_order.utm')}</Typography>
              {orderOrVoucher?.utm_data &&
                Object.entries(orderOrVoucher.utm_data).map(([key, value]) => (
                  <Typography key={key}>{`${key.split('_').join(' ')}: ${value}`}</Typography>
                ))}
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
